
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { PaymentInfo } from '@/hooks/useCourierPayments';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface AuthPaymentCardProps {
  authPayment: PaymentInfo | null;
  onAuthorize: (payment: PaymentInfo) => void;
  loading?: boolean;
}

export const AuthPaymentCard: React.FC<AuthPaymentCardProps> = ({ 
  authPayment, 
  onAuthorize,
  loading = false
}) => {
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  
  if (!authPayment) return null;

  const handleAuthorize = async () => {
    try {
      setIsAuthLoading(true);
      await onAuthorize(authPayment);
    } finally {
      setIsAuthLoading(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования даты', error);
      return dateString;
    }
  };

  return (
    <Card className="w-full bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-900/20 border-orange-200 dark:border-orange-800 shadow-md overflow-hidden">
      <CardHeader className="pb-2 pt-5">
        <CardTitle className="text-center text-lg md:text-xl flex items-center justify-center gap-2">
          <CreditCard className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          Требуется авторизация карты
        </CardTitle>
      </CardHeader>
      
      <CardContent className="pb-6">
        <div className="space-y-4">
          <div className="text-center text-sm text-muted-foreground max-w-md mx-auto">
            <p>Для продолжения работы с сервисом необходимо авторизовать вашу карту.</p>
            <p className="mt-1">Это бесплатная процедура, с карты не будут списаны средства.</p>
          </div>
          
          <div className="bg-white dark:bg-black/20 p-4 rounded-md border border-orange-100 dark:border-orange-900 mb-4">
            <p className="text-center font-medium">
              Запрос от {formatDate(authPayment.date)}
            </p>
            {authPayment.rentalHumanNumber && (
              <p className="text-center text-sm text-muted-foreground mt-1">
                Связанная аренда: {authPayment.rentalHumanNumber}
              </p>
            )}
          </div>
          
          <Button 
            className="w-full py-6 text-base font-medium bg-amber-600 hover:bg-amber-700 transition-all"
            onClick={handleAuthorize}
            disabled={loading || isAuthLoading}
          >
            {loading || isAuthLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Загрузка...
              </>
            ) : (
              <>
                Авторизовать карту
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
